import { useEffect, useMemo, useState, useCallback } from 'react'
import styled from 'styled-components'
import _ from 'lodash'
import { useTranslation } from 'react-i18next'

import { buildAgentConfiguration } from '../../utils/ai/buildAgentConfiguration'
import { buildAgentUrl } from '../../utils/ai/buildAgentUrl'
import { useSessionStorage } from '../../hooks'
import { Flex } from '../Flex'
import { Button } from '../Button'
import { Image } from '../Image'
import { Box } from '../Box'

export const ModalWidgetContainer = styled(Flex)`
  position: fixed;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 10000000;
  background: ${({ theme }) => theme.colors.bgPrimary}80;
`

export const StyledFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: 0;
  margin: 0;
  padding: 0;
  border: solid ${({ theme }) => theme.colors.borderPrimary} 1px;
`

export const AiChatBotWidget = ({
  close,
  domainCode,
  userProfile,
  reportId,
  countryCode,
  refreshCookieIntervalMs
}: {
  close: () => void
  domainCode: string
  userProfile:
    | {
        userId: string | undefined
        subscriberExternalId: string | undefined
        paymentOptionCode: 'PPR' | string | undefined
        isAuthorizedForOrdering: boolean
      }
    | undefined
  reportId?: string
  countryCode?: string
  refreshCookieIntervalMs: number | undefined
}) => {
  const { t } = useTranslation('core')
  const isLoggedIn = !!userProfile?.userId

  const [{ setup: setupBackupBodyOverflow, value: backupBodyOverflow }, setBackupBodyOverflow] = useState<{
    setup: boolean
    value?: string
  }>({ setup: false })
  useEffect(() => {
    if (!setupBackupBodyOverflow) {
      setBackupBodyOverflow({ setup: true, value: document.body.style.overflow })
    }
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = backupBodyOverflow ?? ''
    }
  }, [setupBackupBodyOverflow, backupBodyOverflow, setBackupBodyOverflow])

  const { userId } = userProfile ?? {}

  const sessionStorageKey = `conversation-${domainCode ?? `unknown-domain`}${userId ? `-${userId}` : ''}${reportId ? `-${reportId}` : ''}`
  const [conversationId] = useSessionStorage(sessionStorageKey, undefined)
  const conversationIdSessionStorage = useSessionStorage(sessionStorageKey, undefined)
  const setConversationId = conversationIdSessionStorage[1]
  const clearConversationId = conversationIdSessionStorage[2]

  const onConversationSelectedEventHandler = useCallback(
    (event?: Event) => {
      if ((event as any)?.origin !== window.location.origin) {
        return
      }

      if ((event as any)?.data?.name === 'onConversationSelected') {
        const newSelectedConversationId = (event as any)?.data?.current?.conversation_id
        if (newSelectedConversationId) {
          setConversationId(newSelectedConversationId)
        } else {
          clearConversationId()
        }
      }
    },
    [clearConversationId, setConversationId]
  )

  useEffect(() => {
    window.addEventListener('message', onConversationSelectedEventHandler)

    return () => window.removeEventListener('message', onConversationSelectedEventHandler)
  }, [onConversationSelectedEventHandler])

  const closeOnEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.code === 'Escape') {
        close()
      }
    },
    [close]
  )

  useEffect(() => {
    window.addEventListener('keyup', closeOnEscape)

    return () => window.removeEventListener('keyup', closeOnEscape)
  }, [closeOnEscape])

  useEffect(() => {
    if (isLoggedIn) {
      const intervalId = setInterval(() => {
        fetch('/api/keepalive/ping')
      }, refreshCookieIntervalMs)

      return () => {
        clearInterval(intervalId)
      }
    }
  }, [isLoggedIn, refreshCookieIntervalMs])

  const assistantConfiguration = useMemo(
    () =>
      buildAgentConfiguration({
        lang: 'en',
        briqDomain: `briq_${_.toLower(domainCode)}`,
        autoStart: !conversationId,
        reportId,
        countryCode,
        conversationId
      }),
    [conversationId, countryCode, domainCode, reportId]
  )

  return (
    <ModalWidgetContainer flexDirection="column">
      <Box alignSelf="end">
        <Button variant="primary" icon={<Image type="closeWhite" />} onClick={close}>
          {t('core:Close')}
        </Button>
      </Box>
      <StyledFrame id="ai-widget" src={buildAgentUrl(assistantConfiguration)} />
    </ModalWidgetContainer>
  )
}
